import { useEffect } from 'react';
import { noOpenModal } from '../service/api';
import { checkPopShow } from '../service/strategy/priority';
import { preloadLazyLoad } from '../service/strategy/dataStorage';
import { closePopShowNext0 } from '../service/strategy/show';
import { companyUUID } from '../service/api';
import { shopify, xike } from '../constant/company';
import { MISSACC, HELLOICE, AOOLIA, DRAW, HEYPROM, SMALLRIG, STORAGE_KEY } from '../constant/constant';
import { setSession } from '@/utils/storage';
import { popupFlowLog } from '@/utils/popupFlowDebug';

/** 支持购物车/加购逻辑的平台列表（加购触发需在购物车页或有加购标记） */
const CART_PLATFORMS = [
  'shopify', 'shopifyTest', 'shopline', 'shoplineTest',
  'shoplazza', 'shoplazzaTest', 'shopyy', 'shopyyTest'
];
/** 当前是否为上述平台之一 */
const isCartPlatform = () => CART_PLATFORMS.some(env => process.env.REACT_APP_DEPLOY_ENV === env);

/**
 * 初始触发逻辑（仅 mount 执行一次）：点击/icon 恢复/曝光策略/转盘/倒计时/下滑/加购/离开/弃单/加购跳转
 */
export function useTriggerLogic(options: {
  setShow: (v: boolean, from?: string) => void;
  setIconShow: (v: boolean) => void;
  settings: any;
  responseData: any;
  id: string;
  triggerEventType: number;
  combinationType?: number;
  expand?: boolean;
  /** 已过去时间（毫秒），用于计算剩余延迟 */
  extraTime?: number;
  actionType?: number;
  /** 触发来源（如 leaveBeta） */
  triggerSource?: string;
  /** 若需先拉折扣再展示则执行 checkDiscountLogic 后 callback，否则直接 callback */
  openWithDiscountCheck: (callback: () => void) => void;
}) {
  const {
    setShow,
    setIconShow,
    settings,
    responseData,
    id,
    triggerEventType,
    combinationType,
    expand,
    extraTime = 0,
    actionType,
    triggerSource,
    openWithDiscountCheck
  } = options;

  useEffect(() => {
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const removeListeners: (() => void)[] = [];
    const cleanup = () => {
      timeouts.forEach((id) => clearTimeout(id));
      removeListeners.forEach((fn) => fn());
    };

    /** 点击类触发（6/8）直接展示 点击触发(6/8)：用户点击按钮/链接后直接展示，不校验延迟与曝光策略*/
    if ([6, 8].includes(triggerEventType)) {
      setShow(true, 'click');
      return cleanup;
    }
    /** 从 localStorage 恢复：与当前 id 一致则直接展示（icon 点击恢复） */
    const triggerPop = localStorage?.getItem('_cartsee_tempPop');
    if (triggerPop) {
      try {
        const res = JSON.parse(triggerPop);
        if (res?.id === id) {
          setShow(true);
          return cleanup;
        }
      } catch (_) {}
    }
    /** 指定公司 + 延迟触发(0)：应用曝光策略，不满足则只展示 icon */
    // 页面进入/延迟(0)：曝光策略不通过时只展示 icon 不弹窗
    if ([shopify[30], xike[313], xike[450], shopify[721]].includes(companyUUID)) {
      if (triggerEventType === 0 && !checkPopShow()) {
        popupFlowLog(
          'useTriggerLogic blocked by checkPopShow=false triggerEventType=%s companyUUID=%s popId=%s',
          triggerEventType,
          companyUUID,
          id
        );
        setIconShow(settings?.position?.iconShow === 1);
        setShow(false);
        closePopShowNext0();
        return cleanup;
      }
    }

    /** 预加载后根据是否已有弹窗决定展示弹窗或 icon */
    const showOrIcon = () => {
      const canOpen = noOpenModal();
      popupFlowLog(
        'useTriggerLogic showOrIcon triggerEventType=%s triggerSource=%s canOpen=%s iconShow=%s popId=%s',
        triggerEventType,
        triggerSource ?? '',
        canOpen,
        settings?.position?.iconShow === 1,
        id
      );
      if (canOpen) {
        popupFlowLog('useTriggerLogic showOrIcon -> setShow(true) popId=%s', id);
        setShow(true);
      } else {
        popupFlowLog('useTriggerLogic showOrIcon -> fallback icon/tempPop popId=%s', id);
        setIconShow(settings?.position?.iconShow === 1);
        setShow(false);
        closePopShowNext0();
      }
    };

    /** 转盘：按 timing.afterSeconds 延迟或直接展示 icon */
    if (settings?.timing) {
      if (expand) {
        /** 剩余延迟（毫秒），不小于 0 */
        const timer = Math.max(0, (settings.timing.afterSeconds || 0) * 1000 - extraTime);
        timeouts.push(
          setTimeout(() => {
            preloadLazyLoad(responseData, showOrIcon);
          }, timer)
        );
      } else {
        setIconShow(settings?.position?.iconShow === 1);
        closePopShowNext0();
      }
      return cleanup;
    }

    if (!settings?.rules) return cleanup;
    const isLeaveTriggered = triggerSource === 'leaveBeta' || actionType === 1;

    // 新用户订阅(type=0)：倒计时/下滑/leaveBeta 支持并行多条件 OR
    if (triggerEventType === 0) {
      let hasTriggerCondition = false;
      let opened = false;
      const openOnce = () => {
        if (opened) return;
        opened = true;
        preloadLazyLoad(responseData, showOrIcon);
        cleanup();
      };

      if (settings.rules.loadingCountDownShow) {
        hasTriggerCondition = true;
        let timer = (settings.rules.loadingCountDown || 0) * 1000 - extraTime;
        if (combinationType === 2 || timer < 0) timer = 0;
        timeouts.push(setTimeout(openOnce, timer));
      }

      if (settings.rules.loadingPageSlideShow) {
        hasTriggerCondition = true;
        const scroll = () => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
          const innerHeight = window.innerHeight;
          const scrollHeight = document.body.scrollHeight;
          if (settings.rules?.loadingPageSlideType === 'Percentage') {
            const percent = Math.floor(((scrollTop + innerHeight) / scrollHeight) * 100);
            if (percent >= (settings.rules.loadingPageSlide || 0) || combinationType === 2) {
              openOnce();
              return true;
            }
          } else if (settings.rules?.loadingPageSlideType === 'Pixel') {
            if (scrollTop >= (settings.rules.loadingPageSlidePixel || 0) || combinationType === 2) {
              openOnce();
              return true;
            }
          }
          return false;
        };
        if (!scroll()) {
          window.addEventListener('scroll', scroll);
          removeListeners.push(() => window.removeEventListener('scroll', scroll));
        }
      }

      if (isLeaveTriggered && (settings.rules.readyToLeave || settings.rules.readyToLeaveBate)) {
        hasTriggerCondition = true;
        if (noOpenModal()) openOnce();
      }

      if (hasTriggerCondition) return cleanup;
    }

    /** 倒计时触发：loadingCountDown 秒后展示或 icon */
    // 是否开启「倒计时 N 秒后弹窗」
    if (settings.rules.loadingCountDownShow) {
      let timer = (settings.rules.loadingCountDown || 0) * 1000 - extraTime;
      if (combinationType === 2 || timer < 0) timer = 0;
      timeouts.push(setTimeout(() => preloadLazyLoad(responseData, showOrIcon), timer));
      return cleanup;
    }

    /** 页面下滑触发：按百分比或像素阈值监听 scroll */
    // 是否开启「页面下滑到一定比例/像素后弹窗」
    if (settings.rules.loadingPageSlideShow) {
      const scroll = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
        const innerHeight = window.innerHeight;
        const scrollHeight = document.body.scrollHeight;
        // 下滑按「滚动百分比」触发
        if (settings.rules?.loadingPageSlideType === 'Percentage') {
          const percent = Math.floor(((scrollTop + innerHeight) / scrollHeight) * 100);
          if (percent >= (settings.rules.loadingPageSlide || 0) || combinationType === 2) {
            if (noOpenModal()) {
              preloadLazyLoad(responseData, () => {
                setShow(true);
                window.removeEventListener('scroll', scroll);
              });
              return true;
            }
          }
        // 下滑按「滚动像素」触发
        } else if (settings.rules?.loadingPageSlideType === 'Pixel') {
          if (scrollTop >= (settings.rules.loadingPageSlidePixel || 0) || combinationType === 2) {
            if (noOpenModal()) {
              preloadLazyLoad(responseData, () => {
                setShow(true);
                window.removeEventListener('scroll', scroll);
              });
              return true;
            }
          }
        }
        return false;
      };
      if (!scroll()) {
        window.addEventListener('scroll', scroll);
        removeListeners.push(() => window.removeEventListener('scroll', scroll));
      }
      return cleanup;
    }

    /** 加购触发：延迟后若在购物车页或有加购标记则展示，否则展示 icon */
    if (settings.rules.addPurchaseDownShow) {
      const addPurchaseTimer = Math.max(0, (settings.rules.addPurchaseDown || 0) * 1000 - extraTime);
      timeouts.push(
        setTimeout(() => {
          preloadLazyLoad(responseData, () => {
            if (!noOpenModal()) return;
            if (isCartPlatform()) {
              if (sessionStorage?.getItem('_cartsee_state') === '1' || (window?.location?.pathname?.indexOf('/cart') ?? -1) !== -1) {
                setShow(true);
              } else {
                setIconShow(settings?.position?.iconShow === 1);
              }
            } else {
              setShow(true);
              closePopShowNext0();
            }
          });
        }, addPurchaseTimer)
      );
      return cleanup;
    }

    /** 离开页面 / 优惠券链接跳转：满足则展示，可选先拉折扣码 */
    if (settings.rules.readyToLeave || settings.rules.readyToLeaveBate || settings.rules.couponLinkJump) {
      if (triggerEventType === 0 && !isLeaveTriggered) {
        return cleanup;
      }
      if (noOpenModal()) {
        const needDiscount = settings.rules?.couponCodeShow && settings.rules?.couponCodePosition;
        if (needDiscount === 'start') openWithDiscountCheck(() => setShow(true));
        else setShow(true);
      } else {
      }
      return cleanup;
    }

    /** 弃单离开：actionType 1 立即判断，5 监听 popstate */
    if (settings.rules.discardBillLeave) {
      if (actionType === 1) {
        if (noOpenModal()) {
          const needDiscount = settings.rules?.couponCodeShow && settings.rules?.couponCodePosition;
          if (needDiscount === 'start') openWithDiscountCheck(() => setShow(true));
          else setShow(true);
        }
      } else if (actionType === 5) {
        /** 推一个 history 以便用户点击返回时触发 popstate */
        const pushHistory = () => {
          window.history.pushState({ title: 'init', url: window.location.pathname }, 'init', '#1');
        };
        if (![...MISSACC, ...HELLOICE, ...AOOLIA, ...DRAW, ...HEYPROM, ...SMALLRIG, 'mknrwsqi'].includes(companyUUID)) {
          pushHistory();
        }
        /** 用户点击返回时展示弃单弹窗 */
        const popstateFunc = () => {
          if (noOpenModal()) {
            const needDiscount = settings.rules?.couponCodeShow && settings.rules?.couponCodePosition;
            if (needDiscount === 'start') openWithDiscountCheck(() => setShow(true));
            else setShow(true);
            window.removeEventListener('popstate', popstateFunc);
            setSession(STORAGE_KEY.GIVE_UP_ORDER, 'true');
          }
        };
        window.addEventListener('popstate', popstateFunc, false);
        removeListeners.push(() => window.removeEventListener('popstate', popstateFunc, false));
      }
      return cleanup;
    }

    /** 加购跳转页展示：与加购逻辑类似，并清除跳转相关 sessionStorage */
    if (settings.rules.addPurchaseJumpPageShow) {
      timeouts.push(
        setTimeout(() => {
          preloadLazyLoad(responseData, () => {
            if (!noOpenModal()) return;
            if (isCartPlatform()) {
              const addCartState = sessionStorage?.getItem('_cartsee_state') === '1';
              const addCartJump = sessionStorage?.getItem('_cartsee_add_cart_jump') === '1';
              const isCartPath = (window?.location?.pathname?.indexOf('/cart') ?? -1) !== -1;
              if (addCartState || addCartJump || isCartPath) {
                setShow(true);
                sessionStorage?.removeItem('_cartsee_add_cart_url');
                sessionStorage?.removeItem('_cartsee_add_cart_jump');
                sessionStorage?.removeItem('_cartsee_cart_last_image');
              } else {
                setIconShow(settings?.position?.iconShow === 1);
              }
            } else {
              setShow(true);
              sessionStorage?.removeItem('_cartsee_add_cart_url');
              sessionStorage?.removeItem('_cartsee_add_cart_jump');
              sessionStorage?.removeItem('_cartsee_cart_last_image');
              closePopShowNext0();
            }
          });
        }, 0)
      );
    }
    return cleanup;
  }, []);
}
